import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MapPin, Clock, Store, Heart, Search, Briefcase, CheckCircle2, ArrowLeft, Send, Banknote, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth, startLogin } from "../lib/auth";
import { DISTRICTS, CATEGORIES, fmtDate } from "../lib/options";
import { JobCard } from "../components/JobCard";
import { Empty, Spinner, PageTitle } from "../components/Bits";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";

export function useMarks() {
  const { user } = useAuth();
  const [marks, setMarks] = useState({ applied: [], saved: [] });
  const load = useCallback(() => {
    if (user?.role === "candidate") api.get("/candidate/applied-ids").then((r) => setMarks(r.data)).catch(() => {});
  }, [user]);
  useEffect(() => { load(); }, [load]);
  const toggleSave = async (id) => {
    if (user?.role !== "candidate") return startLogin(window.location.pathname, "candidate");
    const isSaved = marks.saved.includes(id);
    setMarks((m) => ({ ...m, saved: isSaved ? m.saved.filter((x) => x !== id) : [...m.saved, id] }));
    try {
      await (isSaved ? api.delete(`/candidate/saved/${id}`) : api.post(`/candidate/saved/${id}`));
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };
  return { marks, toggleSave, reload: load };
}

const sel = "field !min-h-[52px] !text-base appearance-none";

export function JobsPage() {
  const { t } = useT();
  const [sp, setSp] = useSearchParams();
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState(sp.get("q") || "");
  const { marks, toggleSave } = useMarks();
  const location = sp.get("location") || "";
  const category = sp.get("category") || "";

  useEffect(() => {
    setJobs(null);
    setError(false);
    const params = {};
    ["q", "location", "category"].forEach((k) => sp.get(k) && (params[k] = sp.get(k)));
    api.get("/jobs", { params }).then((r) => setJobs(r.data)).catch(() => setError(true));
  }, [sp]);

  const setParam = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };
  const hasFilters = sp.toString().length > 0;

  return (
    <div className="page pb-10">
      <PageTitle>{t("jobs_title")}</PageTitle>
      <form onSubmit={(e) => { e.preventDefault(); setParam("q", q.trim()); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_ph")} className="field !pl-12" data-testid="jobs-search-input" />
        </div>
        <button className="btn-navy !w-auto !px-5" data-testid="jobs-search-btn" aria-label={t("search")}><Search size={20} /></button>
      </form>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <select value={location} onChange={(e) => setParam("location", e.target.value)} className={sel} data-testid="jobs-filter-location" aria-label={t("place_l")}>
          <option value="">{t("all_places")}</option>
          {DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={category} onChange={(e) => setParam("category", e.target.value)} className={sel} data-testid="jobs-filter-category" aria-label={t("pj_cat")}>
          <option value="">{t("all_types")}</option>
          {CATEGORIES.map(({ v }) => <option key={v} value={v}>{t(`cat_${v}`)}</option>)}
        </select>
      </div>
      <div className="mt-6">
        {error ? (
          <Empty icon={Briefcase} title={t("error")} testid="jobs-error"><button className="btn-orange" onClick={() => setSp(new URLSearchParams(sp))} data-testid="jobs-retry-btn">{t("retry")}</button></Empty>
        ) : jobs === null ? <Spinner label={t("loading")} /> : jobs.length === 0 ? (
          <Empty icon={Briefcase} title={t("no_jobs_t")} sub={t("no_jobs_sub")} testid="jobs-empty-state">
            <Link to="/start" className="btn-orange" data-testid="jobs-empty-create-profile">{t("create_profile")}</Link>
            {hasFilters && <button className="btn-ghost" onClick={() => { setQ(""); setSp(new URLSearchParams()); }} data-testid="jobs-clear-filters">{t("clear_filters")}</button>}
          </Empty>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="jobs-list">
            {jobs.map((j) => <JobCard key={j.id} job={j} saved={marks.saved.includes(j.id)} onToggleSave={toggleSave} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4">
      <Icon size={22} className="mt-0.5 shrink-0 text-[color:var(--orange)]" />
      <div><p className="text-sm font-semibold text-slate-500">{label}</p><p className="font-bold text-slate-900">{value}</p></div>
    </div>
  );
}

function ApplyDone({ open, onClose }) {
  const { t } = useT();
  const nav = useNavigate();
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="apply-success-dialog">
        <div className="flex flex-col items-center text-center">
          <CheckCircle2 size={64} className="text-emerald-500" />
          <DialogTitle className="mt-3 text-2xl font-bold text-[color:var(--navy)]">{t("apply_ok_t")}</DialogTitle>
          <DialogDescription className="mt-1 font-semibold text-slate-700">{t("apply_ok_sub")}</DialogDescription>
        </div>
        <ol className="mt-2 space-y-3">
          {["next1", "next2", "next3"].map((k, i) => (
            <li key={k} className="flex items-start gap-3"><span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[color:var(--navy)] font-bold text-white">{i + 1}</span><span className="pt-1 text-slate-700">{t(k)}</span></li>
          ))}
        </ol>
        <button className="btn-orange !w-full mt-2" onClick={() => nav("/me/applications")} data-testid="apply-success-my-apps-btn">{t("my_apps")}</button>
        <button className="btn-ghost !w-full" onClick={onClose} data-testid="apply-success-ok-btn">{t("ok")}</button>
      </DialogContent>
    </Dialog>
  );
}

export function JobDetail() {
  const { id } = useParams();
  const { t, lang } = useT();
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [job, setJob] = useState(null);
  const [gone, setGone] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const { marks, toggleSave, reload } = useMarks();
  const applied = marks.applied.includes(id);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setGone(true));
  }, [id]);

  const startApply = useCallback(async () => {
    if (!user) return startLogin(`/jobs/${id}?apply=1`, "candidate");
    if (user.role !== "candidate") return toast.error(t("role_cand"));
    try {
      const { data: p } = await api.get("/candidate/profile");
      if (!p.completed) return nav(`/start?next=${encodeURIComponent(`/jobs/${id}?apply=1`)}`);
      setConfirm(true);
    } catch (e) { toast.error(errMsg(e)); }
  }, [user, id, nav, t]);

  useEffect(() => {
    if (sp.get("apply") === "1" && user?.role === "candidate" && job && !applied) startApply();
  }, [sp, user, job, applied, startApply]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/apply`, { message: msg || null });
      setConfirm(false);
      setDone(true);
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (gone) return <div className="narrow py-10"><Empty icon={Briefcase} title={t("job_gone")} testid="job-gone"><Link to="/jobs" className="btn-orange" data-testid="job-gone-back">{t("see_jobs")}</Link></Empty></div>;
  if (!job) return <Spinner label={t("loading")} />;
  const org = job.organizations || {};
  return (
    <div className="narrow pb-40 pt-4 md:pb-12" data-testid="job-detail">
      <button onClick={() => nav(-1)} className="mb-2 inline-flex min-h-[44px] items-center gap-2 font-semibold text-slate-600" data-testid="job-back-btn"><ArrowLeft size={20} />{t("back")}</button>
      <div className="flex items-start gap-4">
        {org.logo_url ? <img src={org.logo_url} alt="" className="h-16 w-16 rounded-2xl border object-cover" /> : <span className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-slate-100 text-[color:var(--navy)]"><Store size={28} /></span>}
        <div className="flex-1">
          <h1 className="text-3xl font-bold leading-tight text-[color:var(--navy)]" data-testid="job-title">{job.title}</h1>
          <p className="text-lg text-slate-600">{org.name}</p>
        </div>
        <button onClick={() => toggleSave(job.id)} className="grid h-12 w-12 place-items-center rounded-full border-2 border-slate-200" data-testid="job-detail-save-btn" aria-label={t("save_job")}>
          <Heart size={24} className={marks.saved.includes(job.id) ? "fill-[color:var(--orange)] text-[color:var(--orange)]" : "text-slate-400"} />
        </button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <Fact icon={MapPin} label={t("place_l")} value={job.branch ? `${job.branch}, ${job.location}` : job.location} />
        <Fact icon={Briefcase} label={t("pj_cat")} value={t(`cat_${job.category}`)} />
        <Fact icon={Clock} label={t("hours_l")} value={(job.hours || []).map((h) => t(`h_${h}`)).join(", ") || "—"} />
        <Fact icon={GraduationCap} label={t("experience_l")} value={job.experience === "none" ? t("e_none_job") : t(`e_${job.experience}`)} />
        {job.salary && <Fact icon={Banknote} label={t("salary")} value={job.salary} />}
      </div>
      {job.description && (<><h2 className="mt-8 text-xl font-bold text-[color:var(--navy)]">{t("about_job")}</h2><p className="mt-2 whitespace-pre-line text-slate-700">{job.description}</p></>)}
      <p className="mt-6 text-sm text-slate-500">{t("posted")} {fmtDate(job.created_at, lang)}</p>
      <div className={`fixed inset-x-0 ${user?.role === "candidate" ? "bottom-16" : "bottom-0 pb-safe"} z-30 border-t border-slate-200 bg-white/95 p-4 backdrop-blur md:static md:mt-8 md:border-0 md:bg-transparent md:p-0`}>
        {applied ? (
          <div className="btn-big w-full !bg-emerald-50 text-emerald-700" data-testid="job-applied-badge"><CheckCircle2 size={24} />{t("applied")}</div>
        ) : (
          <button className="btn-orange !w-full !min-h-[64px] !text-xl" onClick={startApply} data-testid="btn-apply-now"><Send size={24} />{t("apply")}</button>
        )}
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="apply-confirm-dialog">
          <DialogTitle className="text-2xl font-bold text-[color:var(--navy)]">{t("apply_confirm_t")}</DialogTitle>
          <DialogDescription className="text-slate-600">{t("apply_confirm_sub")} : <b>{org.name}</b></DialogDescription>
          <label className="label mt-2" htmlFor="apply-msg">{t("apply_msg")}</label>
          <textarea id="apply-msg" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} maxLength={1000} className="field !py-3" data-testid="apply-message-input" />
          <button className="btn-orange !w-full" onClick={send} disabled={busy} data-testid="apply-confirm-send-btn"><Send size={20} />{t("apply_send")}</button>
          <button className="btn-ghost !w-full" onClick={() => setConfirm(false)} data-testid="apply-confirm-cancel-btn">{t("cancel")}</button>
        </DialogContent>
      </Dialog>
      <ApplyDone open={done} onClose={() => setDone(false)} />
    </div>
  );
}
